import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, Link2, UserPlus } from 'lucide-react';
import {
  dismissElmoStarter,
  listElmoStarters,
  undoElmoStarterDismissal,
} from '@/api/elmoOnboarding';
import { ApiError } from '@/api/client';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { CardField, DesktopTable, MobileCard, MobileCards } from '@/components/common/responsiveList';
import { formatDate, formatDateTime } from '@/lib/dateFormat';
import { elmoActivity, elmoRefLabel } from '@/lib/elmoRef';
import { useAuth } from '@/auth/useAuth';
import type { ElmoQueueStatus, ElmoStarter } from '@/types/oscar';

const TABS: { key: ElmoQueueStatus | 'all'; label: string }[] = [
  { key: 'Pending', label: 'Pending' },
  { key: 'AlreadyInOscar', label: 'Already in Oscar' },
  // Its own tab rather than a silent filter: these records ARE in the queue and
  // the count stays on screen, so a suppressed category can never be mistaken
  // for an absent one.
  { key: 'InactiveInElmo', label: 'Inactive in ELMO' },
  { key: 'Approved', label: 'Approved' },
  { key: 'Dismissed', label: 'Dismissed' },
  { key: 'all', label: 'All' },
];

/**
 * Admin → Onboarding → New starters (PR-36).
 *
 * The pre-start employees ELMO holds until their first day, and the human
 * approval that turns one into an `oscar.Person`.
 *
 * The staleness banner is the load-bearing part of this page. The queue reads a
 * landing refreshed by a MANUAL sync in the People Tracker, so "no pending
 * starters" and "nobody has synced" produce an identical empty list. Whenever
 * the server reports `SourceLooksStale`, the empty state says the list cannot be
 * trusted instead of congratulating the operator.
 */
export function NewStarters() {
  const [tab, setTab] = useState<ElmoQueueStatus | 'all'>('Pending');
  const [dismissing, setDismissing] = useState<ElmoStarter | null>(null);
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const queue = useQuery({
    queryKey: ['elmo-starters', tab],
    queryFn: ({ signal }) => listElmoStarters(tab, signal),
    retry: false,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['elmo-starters'] });

  const dismissMutation = useMutation({
    mutationFn: ({ id, why }: { id: string; why: string }) => dismissElmoStarter(id, why),
    onSuccess: () => { setDismissing(null); setReason(''); invalidate(); },
    onError: (err) => setActionError(err instanceof ApiError ? err.message : 'Could not dismiss this starter.'),
  });

  const undoMutation = useMutation({
    mutationFn: (id: string) => undoElmoStarterDismissal(id),
    onSuccess: invalidate,
    onError: (err) => setActionError(err instanceof ApiError ? err.message : 'Could not return this starter to the queue.'),
  });

  const data = queue.data;
  const rows = data?.Items ?? [];

  return (
    <section>
      <h1 className="font-heading text-2xl font-bold text-navy">New starters</h1>
      <p className="mt-1 max-w-3xl text-sm text-darkGrey/80">
        Pre-start employees ELMO is holding until their first day. Approving one creates their
        Oscar person record, which is what lets them be given an account, a device and software.
        Oscar reads this list from the People Tracker&rsquo;s ELMO sync &mdash; it never calls ELMO directly.
      </p>

      {/* Provenance. Shown whether stale or not: knowing the list is 3 hours old
          is as useful as being warned that it is 3 weeks old. */}
      {data && (
        <div
          role={data.SourceLooksStale ? 'alert' : undefined}
          className={
            data.SourceLooksStale
              ? 'mt-4 flex gap-2 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red'
              : 'mt-4 flex gap-2 rounded border border-border bg-bg px-3 py-2 text-sm text-darkGrey/80'
          }
        >
          {data.SourceLooksStale
            ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            : <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />}
          <div>
            {data.SourceLooksStale ? (
              <>
                <strong>This list may be out of date.</strong> {data.StaleReason}
              </>
            ) : (
              <>
                Last ELMO sync: <strong>{data.LastSyncStatus}</strong> on{' '}
                {formatDateTime(data.LastSyncCompletedAtUtc)}
                {data.LastSyncRecordsSeen != null && <> &middot; {data.LastSyncRecordsSeen} records</>}.
              </>
            )}
          </div>
        </div>
      )}

      {actionError && (
        <div role="alert" className="mt-4 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
          {actionError}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-1" role="tablist" aria-label="Starter status">
        {TABS.map((t) => {
          const count =
            t.key === 'Pending' ? data?.PendingCount
            : t.key === 'Approved' ? data?.ApprovedCount
            : t.key === 'Dismissed' ? data?.DismissedCount
            : t.key === 'AlreadyInOscar' ? data?.AlreadyInOscarCount
            : t.key === 'InactiveInElmo' ? data?.InactiveInElmoCount
            : undefined;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => { setTab(t.key); setActionError(null); }}
              className={
                'rounded px-3 py-1.5 text-sm ' +
                (tab === t.key ? 'bg-navy text-white' : 'bg-bg text-darkGrey hover:bg-border/40')
              }
            >
              {t.label}
              {count != null && <span className="ml-1.5 opacity-75">({count})</span>}
            </button>
          );
        })}
      </div>

      {queue.isLoading && <p className="mt-6 text-sm text-darkGrey/70">Loading&hellip;</p>}

      {queue.isError && (
        <div role="alert" className="mt-6 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
          {queue.error instanceof ApiError ? queue.error.message : 'Could not load the new-starter queue.'}
        </div>
      )}

      {!queue.isLoading && !queue.isError && rows.length === 0 && (
        // An empty queue is NOT good news while the source is stale — it is the
        // absence of data. Never render a reassuring empty state over a warning.
        <p className="mt-6 text-sm text-darkGrey/70">
          {data?.SourceLooksStale
            ? 'Nothing to show — but the ELMO sync above is out of date, so this is the absence of data rather than the absence of starters. Re-run the sync in the People Tracker before treating this as clear.'
            : tab === 'Pending'
              ? 'No starters are waiting for approval.'
              : 'Nothing in this view.'}
        </p>
      )}

      {rows.length > 0 && (
        <>
          <DesktopTable className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-darkGrey/70">
                <tr>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Email</th>
                  <th className="px-3 py-2 font-medium">Position</th>
                  <th className="px-3 py-2 font-medium">Location</th>
                  <th className="px-3 py-2 font-medium">Starts</th>
                  <th className="px-3 py-2 font-medium">ELMO</th>
                  <th className="px-3 py-2 font-medium">ELMO staff no.</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.ElmoUserId} className="border-b border-border/60">
                    <td className="px-3 py-2 font-medium text-navy">
                      {displayName(s)}
                      {s.MissingFromElmoSinceUtc && <NoLongerSentBadge since={s.MissingFromElmoSinceUtc} />}
                    </td>
                    <td className="px-3 py-2 text-darkGrey/80">{s.Email ?? '—'}</td>
                    <td className="px-3 py-2"><RefCell name={s.PositionName} value={s.PositionRef} what="position" /></td>
                    <td className="px-3 py-2"><RefCell name={s.LocationName} value={s.LocationRef} what="location" /></td>
                    <td className="px-3 py-2">{formatDate(s.StartDate)}</td>
                    <td className="px-3 py-2"><ElmoActivityCell isActive={s.IsActiveInElmo} /></td>
                    <td className="px-3 py-2 text-darkGrey/70">{s.ElmoEmployeeNumber ?? '—'}</td>
                    <td className="px-3 py-2"><StatusCell starter={s} /></td>
                    <td className="px-3 py-2">
                      <Actions
                        starter={s}
                        isAdmin={isAdmin}
                        onApprove={() => navigate(`/onboarding/new-starters/${encodeURIComponent(s.ElmoUserId)}/approve`)}
                        onDismiss={() => { setActionError(null); setReason(''); setDismissing(s); }}
                        onUndo={() => { setActionError(null); undoMutation.mutate(s.ElmoUserId); }}
                        undoBusy={undoMutation.isPending}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DesktopTable>

          <MobileCards className="mt-4">
            {rows.map((s) => (
              <MobileCard key={s.ElmoUserId}>
                <div className="font-medium text-navy">
                  {displayName(s)}
                  {s.MissingFromElmoSinceUtc && <NoLongerSentBadge since={s.MissingFromElmoSinceUtc} />}
                </div>
                <CardField label="Email">{s.Email ?? '—'}</CardField>
                <CardField label="Position"><RefCell name={s.PositionName} value={s.PositionRef} what="position" /></CardField>
                <CardField label="Location"><RefCell name={s.LocationName} value={s.LocationRef} what="location" /></CardField>
                <CardField label="Starts">{formatDate(s.StartDate)}</CardField>
                <CardField label="ELMO"><ElmoActivityCell isActive={s.IsActiveInElmo} /></CardField>
                <CardField label="ELMO staff no.">{s.ElmoEmployeeNumber ?? '—'}</CardField>
                <CardField label="Status"><StatusCell starter={s} /></CardField>
                <div className="mt-2">
                  <Actions
                    starter={s}
                    isAdmin={isAdmin}
                    onApprove={() => navigate(`/onboarding/new-starters/${encodeURIComponent(s.ElmoUserId)}/approve`)}
                    onDismiss={() => { setActionError(null); setReason(''); setDismissing(s); }}
                    onUndo={() => { setActionError(null); undoMutation.mutate(s.ElmoUserId); }}
                    undoBusy={undoMutation.isPending}
                  />
                </div>
              </MobileCard>
            ))}
          </MobileCards>
        </>
      )}

      <AlertDialog
        open={dismissing !== null}
        onOpenChange={(o) => { if (!o) { setDismissing(null); setReason(''); } }}
        title={`Dismiss ${dismissing ? displayName(dismissing) : ''}?`}
        description="They drop out of the pending queue and no Oscar person is created. An admin can undo this later. A reason is required — it is what stops the dismissal reading as an oversight months from now."
        confirmLabel="Dismiss starter"
        destructive
        closeOnConfirm={false}
        onConfirm={() => {
          if (!dismissing || !reason.trim()) return;
          dismissMutation.mutate({ id: dismissing.ElmoUserId, why: reason.trim() });
        }}
      >
        <label className="mt-3 block text-sm">
          <span className="font-medium text-darkGrey">Reason</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            aria-label="Dismissal reason"
            className="mt-1 w-full rounded border border-border px-3 py-2 text-sm"
            placeholder="e.g. Duplicate ELMO record; the real starter is under another email."
          />
        </label>
      </AlertDialog>
    </section>
  );
}

function displayName(s: ElmoStarter): string {
  return s.FullName ?? [s.FirstName, s.LastName].filter(Boolean).join(' ') ?? s.ElmoUserId;
}

/**
 * ELMO stopped returning this record entirely — a different thing from ELMO
 * returning it marked inactive, which the ELMO column shows. They were one badge
 * originally, which made a terminated person and a dropped record look identical.
 */
function NoLongerSentBadge({ since }: { since: string }) {
  return (
    <span
      className="ml-2 rounded bg-red/10 px-1.5 py-0.5 text-xs text-red"
      title={`ELMO stopped sending this record on ${formatDate(since)}. It may have been withdrawn, or converted to a general user on their start date.`}
    >
      no longer sent
    </span>
  );
}

/**
 * A position / location cell, in preference order:
 *
 *  1. `name` — resolved by the People-side lookup pass. The normal case.
 *  2. a value ELMO sent that is already a name (not every field is a reference).
 *  3. an unresolved reference — say so, with the reference in the tooltip. An
 *     empty cell here would read as "this person has no position", which is a
 *     different and wrong claim.
 *  4. nothing at all — an honest dash.
 */
function RefCell({ name, value, what }: { name: string | null; value: string | null; what: string }) {
  const resolved = name?.trim() || elmoRefLabel(value);
  if (resolved) return <span className="text-darkGrey/80">{resolved}</span>;
  if (value) {
    return (
      <span
        className="text-darkGrey/50"
        title={`ELMO sent an internal reference for this ${what} (${value}) and it has not been resolved to a name. Re-run the ELMO sync in the People Tracker, which resolves them.`}
      >
        not named by ELMO
      </span>
    );
  }
  return <span className="text-darkGrey/50">—</span>;
}

/** What ELMO currently says about the record. Unknown is NOT inactive. */
function ElmoActivityCell({ isActive }: { isActive: boolean | null }) {
  const state = elmoActivity(isActive);
  if (state === 'Active') return <span className="text-green">Active</span>;
  if (state === 'Inactive') {
    return (
      <span
        className="text-red"
        title="ELMO marks this record inactive — the person has left, or ELMO closed the onboarding record. Either way they are not a pending new starter."
      >
        Inactive
      </span>
    );
  }
  return (
    <span className="text-darkGrey/50" title="ELMO did not populate the active flag on this record.">
      Unknown
    </span>
  );
}

function StatusCell({ starter }: { starter: ElmoStarter }) {
  switch (starter.QueueStatus) {
    case 'Approved':
      return (
        <span className="inline-flex items-center gap-1 text-green">
          <CheckCircle2 className="h-4 w-4" aria-hidden />
          Approved
          {starter.OscarPersonId != null && (
            <Link to={`/people/${starter.OscarPersonId}`} className="ml-1 underline">
              #{starter.OscarPersonId}
            </Link>
          )}
        </span>
      );
    case 'AlreadyInOscar':
      return (
        <span className="inline-flex items-center gap-1 text-darkGrey/80">
          <Link2 className="h-4 w-4" aria-hidden />
          Already in Oscar
          {starter.OscarPersonId != null && (
            <Link to={`/people/${starter.OscarPersonId}`} className="ml-1 underline">
              {starter.OscarPersonName ?? `#${starter.OscarPersonId}`}
            </Link>
          )}
        </span>
      );
    case 'InactiveInElmo':
      return (
        <span className="text-darkGrey/70" title="Not offered for approval: ELMO does not hold this record as active.">
          Inactive in ELMO
        </span>
      );
    case 'Dismissed':
      return (
        <span className="text-darkGrey/70" title={starter.DismissReason ?? undefined}>
          Dismissed{starter.DecidedBy ? ` by ${starter.DecidedBy}` : ''}
        </span>
      );
    default:
      return <span className="text-orange">Pending</span>;
  }
}

function Actions({
  starter, isAdmin, onApprove, onDismiss, onUndo, undoBusy,
}: {
  starter: ElmoStarter;
  isAdmin: boolean;
  onApprove: () => void;
  onDismiss: () => void;
  onUndo: () => void;
  undoBusy: boolean;
}) {
  if (starter.QueueStatus === 'Pending') {
    return (
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onApprove}
          className="inline-flex items-center gap-1 rounded bg-navy px-2.5 py-1 text-xs text-white hover:bg-navy/90"
        >
          <UserPlus className="h-3.5 w-3.5" aria-hidden />
          Approve&hellip;
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded border border-border px-2.5 py-1 text-xs text-darkGrey hover:bg-bg"
        >
          Dismiss&hellip;
        </button>
      </div>
    );
  }

  if (starter.QueueStatus === 'Dismissed' && isAdmin) {
    return (
      <button
        type="button"
        onClick={onUndo}
        disabled={undoBusy}
        className="rounded border border-border px-2.5 py-1 text-xs text-darkGrey hover:bg-bg disabled:opacity-50"
      >
        {undoBusy ? 'Working…' : 'Return to queue'}
      </button>
    );
  }

  return <span className="text-xs text-darkGrey/50">—</span>;
}
